import { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { templateApiService } from '../services/templateApiService';
import {
  OfficialApiInbox,
  WhatsAppTemplate,
  TemplateStatus,
  TemplateStatusColors,
  TemplateStatusLabels,
  TemplateCategoryLabels,
  TemplateCategoryIcons
} from '../types/template';
import { CreateTemplateModal } from './CreateTemplateModal';
import { TemplatePreviewModal } from './TemplatePreviewModal';
import toast from 'react-hot-toast';

interface TemplateListProps {
  inbox: OfficialApiInbox;
  onBack: () => void;
}

export function TemplateList({ inbox, onBack }: TemplateListProps) {
  const { theme } = useTheme();
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<WhatsAppTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedTemplateForPreview, setSelectedTemplateForPreview] = useState<WhatsAppTemplate | null>(null);
  
  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<TemplateStatus | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  
  // Modal de confirmação de exclusão
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<WhatsAppTemplate | null>(null);

  useEffect(() => {
    loadTemplates();
  }, [inbox]);

  useEffect(() => {
    applyFilters();
  }, [templates, searchTerm, statusFilter, categoryFilter]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      // console.log(`📋 Carregando templates do número ${inbox.phoneNumber}...`);
      const data = await templateApiService.getTemplates(inbox.providerConfig.phoneNumberId);
      // console.log(`✅ ${data.length} template(s) carregado(s)`);
      setTemplates(data);
    } catch (error: any) {
      console.error('❌ Erro ao carregar templates:', error);
      toast.error(error.message || 'Erro ao carregar templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncTemplates = async () => {
    try {
      setSyncing(true);
      // console.log('🔄 Sincronizando templates...');
      const result = await templateApiService.syncTemplates(inbox.providerConfig.phoneNumberId);
      // console.log('✅ Sincronização concluída:', result);
      
      setTemplates(result.data.templates);
      
      toast.success(
        `Sincronizado! ${result.data.total} template(s)`,
        { duration: 3000 }
      );
    } catch (error: any) {
      console.error('❌ Erro ao sincronizar:', error);
      toast.error(error.message || 'Erro ao sincronizar templates');
    } finally {
      setSyncing(false);
    }
  };

  const handleDeleteTemplate = async (template: WhatsAppTemplate) => {
    setTemplateToDelete(template);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteTemplate = async () => {
    if (!templateToDelete) return;

    try {
      // console.log(`🗑️ Deletando template: ${templateToDelete}`);
      await templateApiService.deleteTemplate(
        inbox.providerConfig.phoneNumberId,
        templateToDelete.name,
        templateToDelete.id
      );
      
      toast.success(`Template "${templateToDelete.name}" deletado com sucesso!`);
      setShowDeleteConfirm(false);
      setTemplateToDelete(null);
      await loadTemplates();
    } catch (error: any) {
      console.error('❌ Erro ao deletar template:', error);
      toast.error(error.message || 'Erro ao deletar template');
    }
  };

  const cancelDeleteTemplate = () => {
    setShowDeleteConfirm(false);
    setTemplateToDelete(null);
  };

  const handlePreviewTemplate = (template: WhatsAppTemplate) => {
    console.log(`👁️ Visualizando template: ${template.name}`);
    setSelectedTemplateForPreview(template);
    setShowPreviewModal(true);
  };

  const applyFilters = () => {
    let filtered = [...templates];

    // Filtro de busca
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(t =>
        t.name.toLowerCase().includes(term) ||
        t.category.toLowerCase().includes(term)
      );
    }

    // Filtro de status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(t => t.status === statusFilter);
    }

    // Filtro de categoria
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(t => t.category === categoryFilter);
    }

    setFilteredTemplates(filtered);
  };

  const getStatusBadge = (status: TemplateStatus) => {
    const color = TemplateStatusColors[status];
    const label = TemplateStatusLabels[status];

    return (
      <span
        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
        style={{
          backgroundColor: `${color}20`,
          color: color
        }}
      >
        {label}
      </span>
    );
  };

  const getStatsCard = () => {
    const stats = {
      total: templates.length,
      approved: templates.filter(t => t.status === 'APPROVED').length,
      pending: templates.filter(t => t.status === 'PENDING').length,
      rejected: templates.filter(t => t.status === 'REJECTED').length
    };

    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total', value: stats.total, color: 'blue', icon: '📋' },
          { label: 'Aprovados', value: stats.approved, color: 'green', icon: '✅' },
          { label: 'Pendentes', value: stats.pending, color: 'yellow', icon: '⏳' },
          { label: 'Rejeitados', value: stats.rejected, color: 'red', icon: '❌' }
        ].map((stat) => (
          <div
            key={stat.label}
            className={`rounded-lg p-4 ${
              theme === 'dark' ? 'bg-[#2A2A2A]' : 'bg-white'
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {stat.label}
                </p>
                <p className={`text-2xl font-bold mt-1 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  {stat.value}
                </p>
              </div>
              <span className="text-3xl">{stat.icon}</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
            Carregando templates...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Estatísticas */}
      {getStatsCard()}

      {/* Ações e Filtros */}
      <div className={`rounded-lg p-4 mb-6 ${
        theme === 'dark' ? 'bg-[#2A2A2A]' : 'bg-white'
      }`}>
        <div className="flex flex-col md:flex-row gap-4">
          {/* Busca */}
          <div className="flex-1">
            <input
              type="text"
              placeholder="Buscar templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
            />
          </div>

          {/* Filtro de Status */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className={`px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              theme === 'dark'
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          >
            <option value="all">Todos os Status</option>
            <option value="APPROVED">Aprovados</option>
            <option value="PENDING">Pendentes</option>
            <option value="REJECTED">Rejeitados</option>
            <option value="DISABLED">Desabilitados</option>
          </select>

          {/* Filtro de Categoria */}
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className={`px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              theme === 'dark'
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          >
            <option value="all">Todas as Categorias</option>
            <option value="MARKETING">Marketing</option>
            <option value="UTILITY">Utilidade</option>
            <option value="AUTHENTICATION">Autenticação</option>
          </select>

          {/* Botão Sincronizar */}
          <button
            onClick={handleSyncTemplates}
            disabled={syncing}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              syncing
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
          >
            {syncing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Sincronizando...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Sincronizar
              </>
            )}
          </button>

          {/* Botão Criar */}
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Criar Template
          </button>
        </div>
      </div>

      {/* Lista de Templates */}
      {filteredTemplates.length === 0 ? (
        <div className={`rounded-lg p-12 text-center ${
          theme === 'dark' ? 'bg-[#2A2A2A]' : 'bg-white'
        }`}>
          <p className={`text-lg ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            {searchTerm || statusFilter !== 'all' || categoryFilter !== 'all'
              ? 'Nenhum template encontrado com os filtros aplicados'
              : 'Nenhum template encontrado. Crie seu primeiro template!'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredTemplates.map((template) => (
            <div
              key={template.id}
              className="rounded-lg p-6 transition-all bg-theme-card hover:shadow-md dark:hover:bg-opacity-90"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {/* Header */}
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-2xl">
                      {TemplateCategoryIcons[template.category]}
                    </span>
                    <div>
                      <h3 className="text-lg font-semibold text-theme-text">
                        {template.name}
                      </h3>
                      <p className="text-sm text-theme-text-secondary">
                        {TemplateCategoryLabels[template.category]} • {template.language}
                      </p>
                    </div>
                  </div>

                  {/* Status */}
                  <div className="mb-3">
                    {getStatusBadge(template.status)}
                  </div>

                  {/* Componentes */}
                  <div className="space-y-2">
                    {template.components.map((comp, idx) => (
                      <div
                        key={idx}
                        className={`text-sm ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}
                      >
                        <span className="font-medium">
                          {comp.type === 'BUTTONS' ? 'Botões' : comp.type}:
                        </span>{' '}
                        {comp.type === 'BUTTONS' ? (
                          <div className="mt-2 space-y-1">
                            {comp.buttons && comp.buttons.length > 0 ? (
                              comp.buttons.map((button, btnIdx) => (
                                <div
                                  key={btnIdx}
                                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium mr-2 mb-1 ${
                                    theme === 'dark'
                                      ? 'bg-blue-900/30 text-blue-300 border border-blue-700'
                                      : 'bg-blue-100 text-blue-700 border border-blue-200'
                                  }`}
                                >
                                  {button.type === 'QUICK_REPLY' && '💬'}
                                  {button.type === 'URL' && '🔗'}
                                  {button.type === 'PHONE_NUMBER' && '📞'}
                                  <span className="ml-1">{button.text}</span>
                                </div>
                              ))
                            ) : (
                              <span className="italic text-gray-500">Nenhum botão</span>
                            )}
                          </div>
                        ) : comp.text ? (
                          <span className="italic">{comp.text.substring(0, 100)}{comp.text.length > 100 ? '...' : ''}</span>
                        ) : (
                          <span>{comp.format}</span>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Motivo de Rejeição */}
                  {template.status === 'REJECTED' && template.rejected_reason && (
                    <div className="mt-4 p-3 rounded-lg bg-red-100 dark:bg-red-900/20 border border-red-400 dark:border-red-800">
                      <p className="text-sm font-medium text-red-900 dark:text-red-400">
                        ❌ Motivo da Rejeição:
                      </p>
                      <p className="text-sm text-red-800 dark:text-red-300 mt-1">
                        {template.rejected_reason}
                      </p>
                    </div>
                  )}
                </div>

                {/* Ações */}
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => handlePreviewTemplate(template)}
                    className={`p-2 rounded-lg transition-colors ${
                      theme === 'dark'
                        ? 'hover:bg-blue-900/30 text-blue-400'
                        : 'hover:bg-blue-50 text-blue-600'
                    }`}
                    title="Visualizar template"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDeleteTemplate(template)}
                    className={`p-2 rounded-lg transition-colors ${
                      theme === 'dark'
                        ? 'hover:bg-red-900/30 text-red-400'
                        : 'hover:bg-red-50 text-red-600'
                    }`}
                    title="Deletar template"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de Criar Template */}
      {showCreateModal && (
        <CreateTemplateModal
          inbox={inbox}
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadTemplates();
          }}
        />
      )}

      {/* Modal de Pré-visualização */}
      <TemplatePreviewModal
        isOpen={showPreviewModal}
        onClose={() => {
          setShowPreviewModal(false);
          setSelectedTemplateForPreview(null);
        }}
        template={selectedTemplateForPreview}
      />

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="rounded-lg shadow-xl max-w-md w-full mx-4" style={{
            background: theme === 'dark' ? 'linear-gradient(135deg, #1e1e1f 0%, #252527 100%)' : '#ffffff'
          }}>
            <div className="p-6">
              <h3 className="text-xl font-semibold mb-4" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>
                Confirmar Exclusão
              </h3>
              <p className="mb-6" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>
                Tem certeza que deseja excluir o template "{templateToDelete?.name}"?
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={cancelDeleteTemplate}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
                  style={{
                    backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#f3f4f6',
                    color: theme === 'dark' ? 'white' : '#374151'
                  }}
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmDeleteTemplate}
                  className="px-4 py-2 rounded-lg text-white text-sm font-medium transition-all"
                  style={{
                    backgroundColor: '#dc2626'
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#b91c1c')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#dc2626')}
                >
                  Excluir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
